import {useState} from 'react'
import {
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {SLIDE_ACCENT, SLIDE_FEATHER_ICON} from '#/lib/slideTokens'
import {type SlideEditorProps} from '#/components/capsules/play/types'
import {StudioTextField} from '#/components/studio/StudioTextField'
import {readableAccent, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {SLIDE_TYPE_META} from '#/types/capsule'
import {type ComplexBlank} from '#/types/capsule'

// ── pure helpers ──────────────────────────────────────────────────────────────

function reconstructSentence(sentence: string, answer: string): string {
  if (!answer) return sentence
  return sentence.replace('___', answer)
}

function selectWordInSentence(
  sentence: string,
  answer: string,
  wordIndex: number,
): {sentence: string; answer: string} {
  const full = reconstructSentence(sentence, answer)
  const words = full.split(' ')
  const newAnswer = words[wordIndex]
  words[wordIndex] = '___'
  return {sentence: words.join(' '), answer: newAnswer}
}

function deselectBlank(
  sentence: string,
  answer: string,
): {sentence: string; answer: string} {
  return {sentence: reconstructSentence(sentence, answer), answer: ''}
}

function uid() {
  return Math.random().toString(36).slice(2)
}

// ── component ─────────────────────────────────────────────────────────────────

export function ComplexFillBlankSlideEditor({
  slide,
  onChange,
  primaryColor: _primaryColor,
  onPreview,
}: SlideEditorProps) {
  const ss = useStyles()
  const chrome = useStudioChrome()
  const {t: l} = useLingui()
  const accent = SLIDE_ACCENT.complex_fill_blank
  // Ink, not fill: the taxonomy's colour tuned to read on the studio surface it
  // is written on (see `readableAccent`). Tints stay on the raw hue.
  const accentInk = readableAccent(accent, chrome)
  const meta = SLIDE_TYPE_META.complex_fill_blank
  const slideIcon = SLIDE_FEATHER_ICON.complex_fill_blank ?? 'help-circle'
  const blanks: ComplexBlank[] = slide.complexBlanks ?? []

  // set of blank ids currently in text-edit mode
  const [editingIds, setEditingIds] = useState<Set<string>>(new Set())
  // per-blank: is "add wrong option" input open?
  const [addOptOpen, setAddOptOpen] = useState<Record<string, boolean>>({})
  // per-blank: draft text for the new wrong option
  const [optDrafts, setOptDrafts] = useState<Record<string, string>>({})

  const isEditing = (id: string) => editingIds.has(id)

  const toggleEdit = (id: string) => {
    setEditingIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const updateBlank = (id: string, patch: Partial<ComplexBlank>) => {
    onChange({
      complexBlanks: blanks.map(b => (b.id === id ? {...b, ...patch} : b)),
    })
  }

  const addBlank = () => {
    const id = uid()
    onChange({
      complexBlanks: [...blanks, {id, sentence: '', answer: '', options: []}],
    })
    // new blank starts in edit mode
    setEditingIds(prev => new Set([...prev, id]))
  }

  const removeBlank = (id: string) => {
    onChange({complexBlanks: blanks.filter(b => b.id !== id)})
    setEditingIds(prev => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const handleWordTap = (blank: ComplexBlank, wordIndex: number) => {
    const full = reconstructSentence(blank.sentence, blank.answer)
    const words = full.split(' ')
    const word = words[wordIndex]
    if (word === blank.answer) {
      const result = deselectBlank(blank.sentence, blank.answer)
      updateBlank(blank.id, result)
    } else {
      const result = selectWordInSentence(
        blank.sentence,
        blank.answer,
        wordIndex,
      )
      updateBlank(blank.id, result)
    }
  }

  const addOption = (blankId: string) => {
    const opt = (optDrafts[blankId] ?? '').trim()
    if (!opt) return
    const blank = blanks.find(b => b.id === blankId)
    if (!blank || blank.options.includes(opt)) return
    updateBlank(blankId, {options: [...blank.options, opt]})
    setOptDrafts(prev => ({...prev, [blankId]: ''}))
    setAddOptOpen(prev => ({...prev, [blankId]: false}))
  }

  const removeOption = (blankId: string, opt: string) => {
    const blank = blanks.find(b => b.id === blankId)
    if (!blank) return
    updateBlank(blankId, {options: blank.options.filter(o => o !== opt)})
  }

  return (
    <ScrollView
      style={ss.root}
      contentContainerStyle={ss.inner}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}>
      {/* Type badge row */}
      {/* Host-provided header (e.g. FlashBook studio) replaces this row */}
      <View style={[ss.topRow, onPreview ? null : {display: 'none'}]}>
        <View
          style={[
            ss.typeBadge,
            {backgroundColor: accent + '22', borderColor: accent + '44'},
          ]}>
          <Feather name={slideIcon} size={12} color={accentInk} />
          <Text style={[ss.typeBadgeText, {color: accentInk}]}>
            {meta?.label ?? slide.type}
          </Text>
        </View>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={onPreview}
          hitSlop={6}
          style={[
            ss.previewBtn,
            {backgroundColor: accent + '18', borderColor: accent + '55'},
          ]}>
          <Feather name="play-circle" size={13} color={accentInk} />
          <Text
            style={[ss.previewBtnText, {color: accentInk}]}>{l`Preview`}</Text>
        </TouchableOpacity>
      </View>

      {/* Question */}
      <StudioTextField
        accessibilityLabel="Text input field"
        accessibilityHint={undefined}
        style={ss.question}
        value={slide.question ?? ''}
        onChangeText={v => onChange({question: v})}
        placeholder={l`Écris ta question ici…`}
        placeholderTextColor={chrome.textFaint}
      />

      {/* Section label + description */}
      <Text style={ss.sectionLabel}>
        {l`DROPDOWN FILL — SENTENCES`.toUpperCase()}
      </Text>
      <Text style={ss.sectionDesc}>
        {l`Each sentence has one blank. Students choose from a dropdown to complete it.`}
      </Text>

      {/* Blank cards */}
      {blanks.map((blank, idx) => {
        const editing = isEditing(blank.id)
        const fullSentence = reconstructSentence(blank.sentence, blank.answer)
        const words = fullSentence ? fullSentence.split(' ') : []
        const hasBlank = !!blank.answer

        return (
          <View
            key={blank.id}
            style={[ss.blankCard, {borderColor: chrome.borderStrong}]}>
            {/* Card header */}
            <View style={ss.cardHeader}>
              <View style={ss.cardHeaderLeft}>
                <View style={[ss.numBadge, {backgroundColor: accent}]}>
                  <Text style={ss.numBadgeText}>{idx + 1}</Text>
                </View>
                <Text style={ss.sentenceLabel}>{l`SENTENCE`}</Text>
              </View>
              <View style={ss.cardHeaderRight}>
                <TouchableOpacity
                  accessibilityRole="button"
                  onPress={() => toggleEdit(blank.id)}
                  style={[ss.editBtn, {borderColor: chrome.borderStrong}]}>
                  <Feather
                    name={editing ? 'check' : 'edit-2'}
                    size={12}
                    color={chrome.textMuted}
                  />
                  <Text style={ss.editBtnText}>
                    {editing ? <Trans>Done</Trans> : <Trans>Edit</Trans>}
                  </Text>
                </TouchableOpacity>
                {editing && (
                  <TouchableOpacity
                    accessibilityRole="button"
                    onPress={() => removeBlank(blank.id)}
                    hitSlop={6}>
                    <Feather
                      name="trash-2"
                      size={13}
                      color={chrome.textFaint}
                    />
                  </TouchableOpacity>
                )}
              </View>
            </View>

            {/* Edit mode: plain text input */}
            {editing ? (
              <StudioTextField
                accessibilityLabel="Text input field"
                accessibilityHint={undefined}
                style={ss.sentenceInput}
                value={fullSentence}
                onChangeText={v => {
                  // store as plain sentence (no blank) while editing
                  updateBlank(blank.id, {sentence: v, answer: ''})
                }}
                placeholder={l`Type the full sentence here…`}
                placeholderTextColor={chrome.textFaint}
                autoFocus
              />
            ) : (
              /* Select mode: word tokens */
              <View>
                {words.length > 0 ? (
                  <>
                    <Text style={ss.tapHint}>
                      {l`Tap the highlighted word to deselect it, or tap another word to change the blank.`}
                    </Text>
                    <View style={ss.tokenRow}>
                      {words.map((word, wIdx) => {
                        const isBlankWord = word === blank.answer && hasBlank
                        return (
                          <TouchableOpacity
                            key={`${word}-${wIdx}`}
                            accessibilityRole="button"
                            onPress={() => handleWordTap(blank, wIdx)}
                            style={[
                              ss.wordToken,
                              isBlankWord && {
                                borderColor: accentInk,
                                borderStyle: 'dashed',
                                backgroundColor: accent + '18',
                              },
                            ]}>
                            <Text
                              style={[
                                ss.wordTokenText,
                                isBlankWord && {
                                  color: accentInk,
                                  fontWeight: '700',
                                },
                              ]}>
                              {word}
                            </Text>
                          </TouchableOpacity>
                        )
                      })}
                    </View>
                    {hasBlank && (
                      <View style={ss.blankIndicator}>
                        <Feather
                          name="check-circle"
                          size={14}
                          color={chrome.success}
                        />
                        <Text style={ss.blankIndicatorLabel}>{l`Blank:`}</Text>
                        <Text style={ss.blankIndicatorWord}>
                          {blank.answer}
                        </Text>
                      </View>
                    )}
                  </>
                ) : (
                  <TouchableOpacity
                    accessibilityRole="button"
                    onPress={() => toggleEdit(blank.id)}
                    style={ss.emptySentencePlaceholder}>
                    <Text style={ss.emptySentencePlaceholderText}>
                      {l`Tap Edit to type your sentence`}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
            )}

            {/* Wrong options */}
            <View style={ss.wrongOptsSection}>
              <Text style={ss.wrongOptsLabel}>{l`WRONG OPTIONS`}</Text>
              <View style={ss.optRow}>
                {blank.options.map(opt => (
                  <View
                    key={opt}
                    style={[
                      ss.optChip,
                      {
                        backgroundColor: chrome.fill,
                        borderColor: chrome.borderStrong,
                      },
                    ]}>
                    <Text style={[ss.optTxt, {color: chrome.textMuted}]}>
                      {opt}
                    </Text>
                    <TouchableOpacity
                      accessibilityRole="button"
                      hitSlop={6}
                      onPress={() => removeOption(blank.id, opt)}>
                      <Feather name="x" size={10} color={chrome.textFaint} />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>

              {/* Add wrong option */}
              {addOptOpen[blank.id] ? (
                <View style={ss.addOptRow}>
                  <TextInput
                    accessibilityLabel="Text input field"
                    accessibilityHint={undefined}
                    style={ss.addOptInput}
                    value={optDrafts[blank.id] ?? ''}
                    onChangeText={v =>
                      setOptDrafts(prev => ({...prev, [blank.id]: v}))
                    }
                    placeholder={l`Wrong option…`}
                    placeholderTextColor={chrome.textFaint}
                    onSubmitEditing={() => addOption(blank.id)}
                    autoFocus
                  />
                  <TouchableOpacity
                    accessibilityRole="button"
                    onPress={() => addOption(blank.id)}
                    style={[
                      ss.addOptConfirmBtn,
                      {backgroundColor: accent + '22'},
                    ]}>
                    <Feather name="check" size={14} color={accentInk} />
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity
                  accessibilityRole="button"
                  onPress={() =>
                    setAddOptOpen(prev => ({...prev, [blank.id]: true}))
                  }
                  style={ss.addOptDashedBtn}>
                  <Feather name="plus" size={12} color={chrome.textFaint} />
                  <Text style={ss.addOptDashedTxt}>{l`add wrong option`}</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )
      })}

      {/* Add sentence */}
      {blanks.length < 6 && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={addBlank}
          style={ss.addBtn}>
          <Feather name="plus" size={14} color={chrome.textFaint} />
          <Text style={ss.addTxt}>{l`Add a sentence`}</Text>
        </TouchableOpacity>
      )}

      {/* Explanation */}
      <View style={ss.explanationWrap}>
        <Text style={ss.explanationLabel}>
          {l`EXPLICATION (optionnelle)`.toUpperCase()}
        </Text>
        <View style={ss.explanationCard}>
          <StudioTextField
            accessibilityLabel="Text input field"
            accessibilityHint={undefined}
            style={ss.explanationInput}
            value={slide.explanation ?? ''}
            onChangeText={v => onChange({explanation: v})}
            placeholder={l`Explication affichée après la réponse…`}
            placeholderTextColor={chrome.textFaint}
            textAlignVertical="top"
          />
        </View>
      </View>
    </ScrollView>
  )
}

// ── styles ────────────────────────────────────────────────────────────────────

const makeStyles = (chrome: StudioChrome) =>
  StyleSheet.create({
    root: {flex: 1, backgroundColor: chrome.frame},
    inner: {padding: 20, paddingBottom: 40, gap: 16},
    topRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    typeBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      alignSelf: 'flex-start',
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    typeBadgeText: {fontSize: 11, fontWeight: '700'},
    previewBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    previewBtnText: {fontSize: 11, fontWeight: '700'},
    question: {
      color: chrome.text,
      fontSize: 20,
      fontWeight: '700',
      lineHeight: 28,
      paddingVertical: 0,
    },
    sectionLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.9,
    },
    sectionDesc: {color: chrome.textFaint, fontSize: 11},
    blankCard: {
      backgroundColor: chrome.fill,
      borderWidth: 1.5,
      borderRadius: 12,
      padding: 12,
      gap: 10,
    },
    cardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    cardHeaderLeft: {flexDirection: 'row', alignItems: 'center', gap: 8},
    cardHeaderRight: {flexDirection: 'row', alignItems: 'center', gap: 8},
    numBadge: {
      width: 22,
      height: 22,
      borderRadius: 6,
      alignItems: 'center',
      justifyContent: 'center',
    },
    numBadgeText: {color: chrome.onAccent, fontSize: 11, fontWeight: '800'},
    sentenceLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.8,
    },
    editBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 8,
      borderWidth: 1,
    },
    editBtnText: {
      color: chrome.textMuted,
      fontSize: 11,
      fontWeight: '600',
    },
    sentenceInput: {
      color: chrome.textMuted,
      fontSize: 14,
      backgroundColor: chrome.fill,
      borderRadius: 8,
      paddingHorizontal: 10,
      paddingVertical: 8,
      textAlignVertical: 'top',
    },
    tapHint: {
      color: chrome.textFaint,
      fontSize: 11,
      marginBottom: 8,
    },
    tokenRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
      backgroundColor: chrome.fill,
      borderRadius: 10,
      padding: 10,
    },
    wordToken: {
      paddingHorizontal: 8,
      paddingVertical: 5,
      borderRadius: 8,
      borderWidth: 1.5,
      borderColor: 'transparent',
      borderStyle: 'solid',
    },
    wordTokenText: {
      color: chrome.text,
      fontSize: 14,
    },
    blankIndicator: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      marginTop: 8,
    },
    blankIndicatorLabel: {
      color: chrome.success,
      fontSize: 13,
      fontWeight: '600',
    },
    blankIndicatorWord: {
      color: chrome.success,
      fontSize: 13,
      fontWeight: '800',
    },
    emptySentencePlaceholder: {
      padding: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      borderStyle: 'dashed',
      alignItems: 'center',
    },
    emptySentencePlaceholderText: {
      color: chrome.textFaint,
      fontSize: 13,
    },
    wrongOptsSection: {gap: 6},
    wrongOptsLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.8,
    },
    optRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 6},
    optChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    optTxt: {fontSize: 12},
    addOptRow: {flexDirection: 'row', gap: 6, alignItems: 'center'},
    addOptInput: {
      flex: 1,
      color: chrome.textMuted,
      fontSize: 12,
      backgroundColor: chrome.fill,
      borderRadius: 8,
      paddingHorizontal: 9,
      paddingVertical: 6,
    },
    addOptConfirmBtn: {padding: 9, borderRadius: 8},
    addOptDashedBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      alignSelf: 'flex-start',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 20,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: chrome.borderStrong,
    },
    addOptDashedTxt: {color: chrome.textFaint, fontSize: 12},
    addBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 10,
      paddingHorizontal: 14,
      borderRadius: 12,
      borderWidth: 1.5,
      borderStyle: 'dashed',
      borderColor: chrome.borderStrong,
    },
    addTxt: {color: chrome.textFaint, fontSize: 13},
    explanationWrap: {gap: 6},
    explanationLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.9,
    },
    explanationCard: {
      backgroundColor: chrome.fill,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    explanationInput: {
      color: chrome.textMuted,
      fontSize: 13,
      lineHeight: 18,
      paddingVertical: 0,
    },
  })

/** The editor's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
